import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useSEO } from '../hooks/useSEO';
import { StructuredData } from '../components/StructuredData';

interface Tool {
    name: string;
    description: string;
    path: string;
    icon: string;
}

export default function Home() {
    const { t } = useI18n();

    // SEO Optimization
    useSEO({
        title: t.home.title,
        description: t.seo.siteDescription,
        keywords: t.seo.keywords,
    });

    const tools: Tool[] = [
        {
            name: t.tools.jsonFormatter.name,
            description: t.tools.jsonFormatter.description,
            path: '/json-formatter',
            icon: '{}',
        },
        {
            name: t.tools.base64.name,
            description: t.tools.base64.description,
            path: '/base64',
            icon: '⇄',
        },
        {
            name: t.tools.uuid.name,
            description: t.tools.uuid.description,
            path: '/uuid-generator',
            icon: '🆔',
        },
        {
            name: t.tools.hash.name,
            description: t.tools.hash.description,
            path: '/hash-generator',
            icon: '#️⃣',
        },
        {
            name: t.tools.urlEncoder.name,
            description: t.tools.urlEncoder.description,
            path: '/url-encoder',
            icon: '🔗',
        },
        {
            name: t.tools.timestamp.name,
            description: t.tools.timestamp.description,
            path: '/timestamp-converter',
            icon: '⏰',
        },
        {
            name: t.tools.color.name,
            description: t.tools.color.description,
            path: '/color-converter',
            icon: '🎨',
        },
        {
            name: t.tools.jwt.name,
            description: t.tools.jwt.description,
            path: '/jwt-decoder',
            icon: '🔐',
        },
        {
            name: t.tools.regex.name,
            description: t.tools.regex.description,
            path: '/regex-tester',
            icon: '🔍',
        },
        {
            name: t.tools.markdown.name,
            description: t.tools.markdown.description,
            path: '/markdown-preview',
            icon: '📝',
        },
        {
            name: t.tools.qrCode.name,
            description: t.tools.qrCode.description,
            path: '/qr-code-generator',
            icon: '📱',
        },
        {
            name: t.tools.lorem.name,
            description: t.tools.lorem.description,
            path: '/lorem-ipsum-generator',
            icon: '📄',
        },
        {
            name: t.tools.htmlEntity.name,
            description: t.tools.htmlEntity.description,
            path: '/html-entity-encoder',
            icon: '🔤',
        },
    ];

    // Prepare tools data for structured data
    const toolsForSchema = tools.map(tool => ({
        name: tool.name,
        description: tool.description,
        url: `${window.location.origin}${tool.path}`,
    }));

    return (
        <>
            <StructuredData tools={toolsForSchema} />
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    {t.home.title}
                </h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    {t.home.subtitle}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tools.map((tool) => (
                    <Link
                        key={tool.path}
                        to={tool.path}
                        className="tool-card hover:border-primary-400"
                    >
                        <div className="text-4xl mb-4">{tool.icon}</div>
                        <h2 className="text-xl font-semibold mb-2">{tool.name}</h2>
                        <p className="text-gray-600">{tool.description}</p>
                    </Link>
                ))}
            </div>

            <div className="mt-16 bg-primary-50 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.home.whyTitle}</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <h3 className="font-semibold text-lg mb-2">{t.home.privacyTitle}</h3>
                        <p className="text-gray-600">{t.home.privacyDesc}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg mb-2">{t.home.fastTitle}</h3>
                        <p className="text-gray-600">{t.home.fastDesc}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg mb-2">{t.home.freeTitle}</h3>
                        <p className="text-gray-600">{t.home.freeDesc}</p>
                    </div>
                </div>
            </div>
        </>
    );
}
